import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import APIManager from "../../../managers/APIManager";
import Loading from "../../common/Loading";
import Button from "../../common/Button";
import { faArrowLeft, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import LanguageSelector from "../../common/LanguageSelector";
import StyleBrandingCollapse from "./StyleBrandingCollapse";
import ContentCollapse from "./ContentCollapse";
import GenerateVideoCollapse from "./GenerateVideoCollapse";
import { useStyleSettings } from "../../../hooks";
import { toast } from "react-toastify";
import {
  templateDataConverter,
  templateDefaultStyleSettings,
} from "../templates";

const AIVideoCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [orderBy, setOrderBy] = useState("text");
  const [orderByDirection, setOrderByDirection] = useState("desc");

  const [loading, setLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [newsList, setNewsList] = useState([]);
  const [selectedNews, setSelectedNews] = useState([]);

  const siteVirgulId = useSelector(
    (state) => state.site?.selectedSite?.virgulId
  );
  const videoCreation = useSelector(
    (state) => state.site?.selectedSite?.videoCreation
  );
  const [selectedVideoTemplate, setSelectedVideoTemplate] = useState(null);
  const [videoTemplates, setVideoTemplates] = useState([]);

  const isItemsLengthValid = useMemo(() => {
    if (!selectedVideoTemplate?.restrictions) {
      return true;
    }
    return selectedVideoTemplate?.restrictions["items[].length"]
      ? selectedNews.length ===
      selectedVideoTemplate?.restrictions["items[].length"]
      : true;
  }, [selectedNews, selectedVideoTemplate]);

  // Use the reducer-based hook for style settings
  const { styleSettings, mergeStyleSettings, handleStyleChange } =
    useStyleSettings();

  // Ref for scrolling container
  const scrollContainerRef = useRef(null);

  const saveVideo = useCallback(
    async (_immediateSave = false) => {
      setGenerating(true);
      setProgress(0);
      try {
        const response = await APIManager.saveAIVideo({
          siteVirgulId,
          template: selectedVideoTemplate.name,
          data: templateDataConverter({
            templateName: selectedVideoTemplate.name,
            styleSettings,
            selectedNews,
            newsList,
          }),
        });

        const videoId = response.data.id;
        setProgress(0.00001);

        // start polling for video status
        await new Promise((resolve) => {
          const pollVideoStatus = async () => {
            try {
              const video = await APIManager.getAIVideo({
                siteVirgulId,
                videoId,
              });
              if (video?.data?.progress > 0) {
                setProgress(Number(video?.data?.progress) * 100);
                if (Number(video?.data?.progress) === 1) {
                  clearInterval(pollVideoStatusInterval);
                  resolve();
                }
              }
            } catch (error) {
              console.error("Error polling video status:", error);
              clearInterval(pollVideoStatusInterval);
              resolve();
            }
          };
          const pollVideoStatusInterval = setInterval(pollVideoStatus, 1000);
        });

        setProgress(100);
        navigate(`/ai-video/${videoId}`);
        toast.success(t("video created successfully"));
      } catch (error) {
        console.error("Error generating video:", error);
        setProgress(0);
      } finally {
        setGenerating(false);
      }
    },
    [siteVirgulId, selectedVideoTemplate, navigate, styleSettings, selectedNews]
  );

  const fetchNews = useCallback(async () => {
    if (!videoCreation || !siteVirgulId) {
      navigate("/");
      return;
    }
    setNewsLoading(true);

    const initialData = location.state;

    // If we have initial data from edit, use it directly
    if (initialData?.initialNewsList) {
      setSelectedNews(initialData.initialSelectedNews || []);
    }

    try {
      const fetchedSiteNewsRes = await APIManager.getFetchedSiteNews({
        siteVirgulId,
        orderBy,
        orderByDirection,
      });
      setNewsList(
        [...(initialData?.initialNewsList || []),
        ...(fetchedSiteNewsRes?.data?.filter((item) => item.title && item.title.trim() !== "")
          .map((item, index) => ({
            ...item,
            id: index + 1,
          })) || []),
        ]
      );
    } catch (error) {
      console.error("Error fetching news:", error);
      setNewsList([]);
    } finally {
      setNewsLoading(false);
    }
  }, [videoCreation, siteVirgulId, navigate, orderBy, orderByDirection, location.state]);

  const fetchVideoTemplates = useCallback(async () => {
    let fetchedVideoTemplatesRes;
    const initialData = location.state;

    try {
      fetchedVideoTemplatesRes = await APIManager.getAIVideoTemplates({
        siteVirgulId,
      });
      setVideoTemplates(fetchedVideoTemplatesRes?.data || []);
    } catch (error) {
      console.error("Error fetching video templates:", error);
      setVideoTemplates([]);
    }

    try {
      // If we have initial data from edit, use it
      if (initialData?.initialTemplate) {
        const template = fetchedVideoTemplatesRes?.data?.find(
          (template) => template.name === initialData.initialTemplate
        );
        if (template) {
          setSelectedVideoTemplate(template);
        }
        if (initialData.initialStyleSettings) {
          mergeStyleSettings({
            ...templateDefaultStyleSettings(initialData.initialTemplate),
            ...initialData.initialStyleSettings,
          });
        }
      } else {
        // Otherwise, use default settings
        const fetchedVideoCreationSettingsRes =
          await APIManager.getAIVideoCreationSettings({
            siteVirgulId,
          });
        setSelectedVideoTemplate(
          fetchedVideoTemplatesRes?.data.find(
            (template) =>
              template.name ===
              fetchedVideoCreationSettingsRes?.data?.templateName
          )
        );
        mergeStyleSettings({
          ...templateDefaultStyleSettings(
            fetchedVideoCreationSettingsRes?.data?.templateName
          ),
          ...fetchedVideoCreationSettingsRes?.data?.templateSettings,
        });
      }
    } catch (error) {
      console.error("Error fetching video templates:", error);
      setVideoTemplates([]);
    }
  }, [siteVirgulId, location.state, mergeStyleSettings]);

  useEffect(() => {
    fetchVideoTemplates().finally(
      fetchNews().finally(() => {
        setLoading(false);
      })
    );
  }, [fetchVideoTemplates, fetchNews]);

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <div className="flex h-screen bg-gray-50">
        <div ref={scrollContainerRef} className="flex-1 p-6 overflow-y-auto">
          <div className="mx-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center">
                <Button
                  className="mr-4"
                  variant="secondary"
                  icon={faArrowLeft}
                  onClick={() => navigate(-1)}
                >
                  {t("back")}
                </Button>

                <h1 className="text-3xl font-bold text-gray-900">
                  {t("create video")}
                </h1>
              </div>
              <LanguageSelector />
            </div>
            <StyleBrandingCollapse
              videoTemplates={videoTemplates}
              selectedVideoTemplate={selectedVideoTemplate}
              setSelectedVideoTemplate={setSelectedVideoTemplate}
              styleSettings={styleSettings}
              mergeStyleSettings={mergeStyleSettings}
              handleStyleChange={handleStyleChange}
            />
            <ContentCollapse
              newsList={newsList}
              newsLoading={newsLoading}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              orderByDirection={orderByDirection}
              setOrderByDirection={setOrderByDirection}
              setNewsList={setNewsList}
              selectedNews={selectedNews}
              setSelectedNews={setSelectedNews}
              styleSettings={styleSettings}
              disabled={!selectedVideoTemplate}
              templateRestrictions={selectedVideoTemplate?.restrictions}
              selectedVideoTemplate={selectedVideoTemplate}
              isItemsLengthValid={isItemsLengthValid}
              itemsLengthRestriction={
                selectedVideoTemplate?.restrictions
                  ? selectedVideoTemplate?.restrictions["items[].length"]
                  : undefined
              }
            />
            <GenerateVideoCollapse
              newsList={newsList}
              selectedVideoTemplate={selectedVideoTemplate}
              selectedNews={selectedNews}
              setSelectedNews={setSelectedNews}
              styleSettings={styleSettings}
              onGenerateVideo={saveVideo}
              loading={generating}
              progress={progress}
              disabled={!selectedVideoTemplate || !selectedNews?.length}
              isItemsLengthValid={isItemsLengthValid}
              itemsLengthRestriction={
                selectedVideoTemplate?.restrictions
                  ? selectedVideoTemplate?.restrictions["items[].length"]
                  : undefined
              }
            />
          </div>
        </div>
      </div>
      {!!selectedNews?.length && isItemsLengthValid && (
        <div className="fixed bottom-0 right-0 z-50 m-6">
          <Button
            color="inverse_primary"
            icon={faChevronDown}
            onClick={scrollToBottom}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
          >
            {t("generate video")}
          </Button>
        </div>
      )}
    </>
  );
};

export default AIVideoCreate;
